import * as THREE from 'three';
import { io, Socket } from 'socket.io-client';
import type { Game, GameContext, GameMeta, InputState } from '../types';
import {
  createThreeKit,
  disposeObject,
  resizeThree,
  type ThreeKit,
} from '../shared/threeKit';

export class BeatPongGame implements Game {
  readonly meta: GameMeta = {
    id: 'pong',
    title: 'BEAT PONG (ONLINE)',
    blurb: '1v1 Online Multiplayer. The arena pulses to the beat.',
    tag: 'MULTIPLAYER',
    status: 'playable',
  };

  private ctx!: GameContext;
  private kit!: ThreeKit;
  private socket!: Socket;
  private disposed = false;
  
  private paddle1!: THREE.Mesh;
  private paddle2!: THREE.Mesh;
  private ball!: THREE.Mesh;
  
  private ballPos = new THREE.Vector2(0, 0);
  private ballVel = new THREE.Vector2(0, 0);
  
  private room = '';
  private playerNum = 0;
  private score = 0;
  private oppScore = 0;
  
  private msg = 'CONNECTING...';
  private paddleSpeed = 20;
  private playing = false;
  private ballActive = false;

  async mount(ctx: GameContext): Promise<void> {
    this.ctx = ctx;
    this.kit = createThreeKit(ctx, {
      label: 'Beat Pong',
      z: 25,
      fov: 45,
      clear: 0x010510,
    });
    
    // Scene setup
    const geo = new THREE.BoxGeometry(0.8, 4, 1);
    const mat = new THREE.MeshStandardMaterial({ color: 0x00ffcc, emissive: 0x005544 });
    const mat2 = new THREE.MeshStandardMaterial({ color: 0xff00cc, emissive: 0x550044 });
    
    this.paddle1 = new THREE.Mesh(geo, mat);
    this.paddle1.position.x = -12;
    this.kit.root.add(this.paddle1);
    
    this.paddle2 = new THREE.Mesh(geo, mat2);
    this.paddle2.position.x = 12;
    this.kit.root.add(this.paddle2);
    
    const ballGeo = new THREE.BoxGeometry(1, 1, 1);
    const ballMat = new THREE.MeshStandardMaterial({ color: 0xffffff, emissive: 0x444444 });
    this.ball = new THREE.Mesh(ballGeo, ballMat);
    this.kit.root.add(this.ball);

    const rect = ctx.mount.getBoundingClientRect();
    this.resize(rect.width || 320, rect.height || 480);
    
    // Socket setup
    this.socket = io({ path: '/socket.io/' });
    
    this.socket.on('connect', () => {
      this.msg = 'WAITING FOR OPPONENT...';
      this.socket.emit('join_pong');
    });
    
    this.socket.on('waiting', () => {
      this.msg = 'WAITING FOR OPPONENT...';
    });
    
    this.socket.on('game_start', (data) => {
      this.room = data.room;
      this.playerNum = data.player;
      this.playing = true;
      this.resetBall();
    });

    this.socket.on('serve_ball', () => {
      if (this.playerNum === 1 && !this.ballActive) {
        this.activateBall();
      }
    });
    
    this.socket.on('opponent_move', (y) => {
      if (this.playerNum === 1) this.paddle2.position.y = y;
      else this.paddle1.position.y = y;
    });
    
    this.socket.on('ball_update', (data) => {
      if (this.playerNum !== 1) {
        this.ballPos.x = data.x;
        this.ballPos.y = data.y;
        this.ball.position.set(data.x, data.y, 0);
        if (data.active && !this.ballActive) {
          this.ballActive = true;
          this.msg = '';
        } else if (!data.active && this.ballActive) {
          this.ballActive = false;
          this.msg = 'PRESS FIRE TO SERVE';
        }
      }
    });
    
    this.socket.on('score_update', (data) => {
      if (this.playerNum !== 1) {
        this.score = data.p2;
        this.oppScore = data.p1;
      }
    });
    
    this.socket.on('opponent_disconnected', () => {
      this.msg = 'OPPONENT LEFT.';
      this.playing = false;
    });
  }
  
  private resetBall() {
    this.ballPos.set(0, 0);
    this.ballVel.set(0, 0);
    this.ball.position.set(0, 0, 0);
    this.ballActive = false;
    this.msg = 'PRESS FIRE TO SERVE';
  }

  private activateBall() {
    this.ballActive = true;
    this.msg = '';
    const toPlayer2 = Math.random() > 0.5;
    const dir = toPlayer2 ? 1 : -1;
    this.ballVel.set(dir * 15, (Math.random() - 0.5) * 10);
  }

  resize(w: number, h: number): void {
    resizeThree(this.kit, w, h);
  }

  update(dt: number, state: InputState): void {
    if (this.disposed) return;
    
    const b = this.ctx.getBands();
    
    // Camera pulse
    const zOffset = b.energy * 2;
    this.kit.camera.position.z = 25 - zOffset;
    
    // Arena pulse
    this.ball.scale.setScalar(1 + b.bass * 0.5);
    
    if (!this.playing) return;

    if (state.firePressed && !this.ballActive) {
      if (this.playerNum === 1) {
        this.activateBall();
      } else {
        this.socket.emit('serve_ball', { room: this.room });
      }
    }
    
    // Local move
    let dy = 0;
    if (state.up) dy = this.paddleSpeed * dt;
    if (state.down) dy = -this.paddleSpeed * dt;
    
    const bounds = 8;
    
    if (this.playerNum === 1) {
      this.paddle1.position.y = Math.max(-bounds, Math.min(bounds, this.paddle1.position.y + dy));
      if (dy !== 0) this.socket.emit('paddle_move', { room: this.room, y: this.paddle1.position.y });
    } else if (this.playerNum === 2) {
      this.paddle2.position.y = Math.max(-bounds, Math.min(bounds, this.paddle2.position.y + dy));
      if (dy !== 0) this.socket.emit('paddle_move', { room: this.room, y: this.paddle2.position.y });
    }
    
    // Host handles physics
    if (this.playerNum === 1) {
      this.ballPos.x += this.ballVel.x * dt;
      this.ballPos.y += this.ballVel.y * dt;
      
      // Top/Bottom bounce
      if (this.ballPos.y > 9) {
        this.ballPos.y = 9;
        this.ballVel.y *= -1;
      }
      if (this.ballPos.y < -9) {
        this.ballPos.y = -9;
        this.ballVel.y *= -1;
      }
      
      // Paddle bounce
      const p1Box = new THREE.Box3().setFromObject(this.paddle1);
      const p2Box = new THREE.Box3().setFromObject(this.paddle2);
      const ballBox = new THREE.Box3().setFromObject(this.ball);
      ballBox.min.x = this.ballPos.x - 0.5; ballBox.max.x = this.ballPos.x + 0.5;
      ballBox.min.y = this.ballPos.y - 0.5; ballBox.max.y = this.ballPos.y + 0.5;
      
      if (this.ballVel.x < 0 && ballBox.intersectsBox(p1Box)) {
        this.ballVel.x *= -1.1; // Speed up
        this.ballPos.x = p1Box.max.x + 0.6;
        this.ballVel.y += (this.ballPos.y - this.paddle1.position.y) * 2;
      } else if (this.ballVel.x > 0 && ballBox.intersectsBox(p2Box)) {
        this.ballVel.x *= -1.1; // Speed up
        this.ballPos.x = p2Box.min.x - 0.6;
        this.ballVel.y += (this.ballPos.y - this.paddle2.position.y) * 2;
      }
      
      // Score
      if (this.ballPos.x < -15) {
        this.oppScore++; // p2 scores
        this.socket.emit('score_update', { room: this.room, p1: this.score, p2: this.oppScore });
        this.resetBall();
      } else if (this.ballPos.x > 15) {
        this.score++; // p1 scores
        this.socket.emit('score_update', { room: this.room, p1: this.score, p2: this.oppScore });
        this.resetBall();
      }
      
      this.ball.position.set(this.ballPos.x, this.ballPos.y, 0);
      this.socket.emit('ball_sync', { room: this.room, x: this.ballPos.x, y: this.ballPos.y, active: this.ballActive });
    }
    
    this.kit.renderer.render(this.kit.scene, this.kit.camera);
    if (this.kit.composer) this.kit.composer.render();
  }

  getHud() {
    return {
      lives: 1, // Doesn't matter
      wave: this.playerNum === 1 ? this.oppScore : this.score, // show opponent score as wave
      score: this.playerNum === 1 ? this.score : this.oppScore, // show my score
      message: this.msg,
    };
  }

  dispose(): void {
    this.disposed = true;
    if (this.socket) {
      this.socket.disconnect();
    }
    disposeObject(this.kit.root);
    this.kit.dispose();
  }
}
